// resolveScope(buffer, cursor): composes alias + CTE + subquery + pseudo-table
// bindings into a full Scope tree for the cursor position. The returned Scope
// is the INNERMOST scope containing the cursor; its `parent` chain climbs to
// the outer statement scope. Reachable tables are the union of aliases + in-
// scope CTEs + pseudo-tables + (at the outermost scope) current-schema tables
// from the SchemaSnapshot.
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Scope Resolver —
// subqueries + pseudo-tables + resolveScope assembly.

import type {
  ParsedBuffer,
  Scope,
  AliasBinding,
  CteBinding,
  ReachableTable,
  SqlPosition,
} from '../types';

import { computeAliasBindingsForRange } from './aliasScope';
import { computeCteBindingsForRange } from './cteScope';
import { findSubqueryFramesAt, type SubqueryFrame } from './subqueryScope';
import { collectPseudoTablesForContext } from './pseudoTables';
import { inferPseudoContext } from './pseudoContext';
import { setOpBranchRange } from './setOpBranchScope';
import { filterParentForLateral } from './lateralFrame';
import { dispatchProceduralBodyClassify } from '../procedural/proceduralResolver';
import { resolveAliasRelation } from '../service/snapshotRelations';

export function resolveScope(buffer: ParsedBuffer, cursor: SqlPosition): Scope {
  const { parsedDocument, documentStructure, scriptScope, schemaSnapshot } = buffer;
  const { options, source } = parsedDocument;
  const offset = Math.min(Math.max(cursor.offset, 0), source.length);

  const stmt = documentStructure.statementAt(offset);
  const currentSchema = scriptScope?.currentSchema ?? options.searchPath?.[0];
  const currentDatabase = scriptScope?.currentDatabase;

  // Empty / no-statement buffer — return the minimal top-level scope.
  if (!stmt) {
    return buildEmptyScope(currentSchema, currentDatabase, schemaSnapshot);
  }

  // Procedural-body: the classifier's dispatch returns a ResolverPositionKind, but the
  // scope contract is that the outer scope is always computed from the wrapper
  // statement; the procedural sub-resolver then builds its own scope via the
  // registry. The outer scope (which this module builds) is still useful as the
  // `parent` the sub-resolver will receive.
  // §4.5 set-op branch scope: in a compound SELECT (UNION/INTERSECT/EXCEPT,
  // Oracle MINUS) an alias declared in one branch's FROM list must not resolve
  // with the cursor in another branch. Narrow the ALIAS scan to the cursor's
  // branch; a leading WITH clause is shared by every branch, so CTEs keep the
  // whole-statement scan (branch-narrowing them would hide a shared CTE from
  // branches 2+). Non-compound / tail / separator positions → null → unchanged.
  const branch = setOpBranchRange(source, stmt.range.startOffset, stmt.range.endOffset, offset, options);
  const outerAliases = computeAliasBindingsForRange(
    buffer, branch ? branch.start : stmt.range.startOffset, branch ? branch.end : stmt.range.endOffset,
  );
  const outerCtes = computeCteBindingsForRange(
    buffer, stmt.range.startOffset, stmt.range.endOffset,
  );
  const outerPseudo = collectPseudoTablesForContext(
    options.engine, inferPseudoContext(buffer, offset, stmt),
  );

  const outerReachable = buildReachableTables(outerAliases, outerCtes, outerPseudo, schemaSnapshot, /*includeSchemaTables*/ true);

  // Pseudo-records (`:NEW`/`:OLD`, `inserted`/`deleted`, `excluded`, …) are
  // AliasBinding-shaped and behave like a table alias whose `.col` is its
  // column. Surface them through `aliases` too — not just `reachableTables` —
  // so a `<pseudo>.col` dotted base resolves via findAliasInScope and carries
  // the binding's `source: 'pseudo'` (the relation contract dotted-base
  // consumers read). Without this the pseudo is only schema-table-reachable
  // and a `:NEW.col` base never resolves to the pseudo record.
  const outer: Scope = {
    aliases: [...outerAliases, ...outerPseudo],
    ctes: outerCtes,
    reachableTables: outerReachable,
    currentSchema: currentSchema ?? undefined,
    currentDatabase: currentDatabase ?? undefined,
    scopeKind: 'statement',
  };

  // Procedural body: hand off to registered resolver (which receives `outer`
  // as its parent). If the sub-resolver returns a ResolverPositionKind (outer-boundary
  // fallback), we still return `outer` — the Scope API is about alias/identifier
  // lookup, not classification.
  if (stmt.kind === 'procedural-body') {
    const delegated = delegateProceduralScope(buffer, cursor, outer);
    if (delegated) return delegated;
    return outer;
  }

  // Walk the subquery frames outermost-first and build a parent-linked chain.
  const frames = findSubqueryFramesAt(buffer, stmt.id, offset);
  let scope: Scope = outer;
  for (const frame of frames) {
    scope = buildFrameScope(buffer, frame, scope, outerCtes, outerPseudo);
  }
  return scope;
}

function buildFrameScope(
  buffer: ParsedBuffer,
  frame: SubqueryFrame,
  parent: Scope,
  outerCtes: readonly CteBinding[],
  outerPseudo: readonly AliasBinding[],
): Scope {
  // KNOWN LIMITATION (§4.5): this frame-level alias scan is NOT set-op
  // branch-narrowed — a compound SELECT inside a CTE body / derived-table
  // subquery sees sibling-branch aliases (safe over-suggest; canonical shape is
  // the recursive-CTE `UNION ALL` body). Statement-level narrowing lives in
  // setOpBranchScope (decision 2's locus); pinned in resolveScope.test.ts,
  // backlog in Ralph/PLAN_SQL_SEMANTICS.md §9.2 Priority 5.
  const aliases = computeAliasBindingsForRange(buffer, frame.innerStart, frame.innerEnd);
  // A subquery may itself start with `WITH` — pick up its local CTEs.
  const innerCtes = computeCteBindingsForRange(buffer, frame.innerStart, frame.innerEnd);

  if (frame.kind === 'cte-def') {
    // §4.5: a CTE body sees ONLY earlier siblings + SELF — never later
    // siblings, never main-query aliases. `mergeCtesUpTo` already excludes
    // SELF (self's body ends after its own start), so add it back explicitly;
    // self-inclusion is unconditional (recursive CTEs on every engine; for
    // pg/mysql/sqlite a non-RECURSIVE self-ref then over-suggests, matching
    // the branch's over-suggest posture and never breaking legal SQL).
    const earlier = mergeCtesUpTo(outerCtes, frame.bodyRange.startOffset);
    const self = outerCtes.find(
      (c) => c.bodyRange.startOffset === frame.bodyRange.startOffset,
    );
    const visible = self ? [...earlier, self] : earlier;
    // Hand the frame a REBUILT filtered parent instead of the leaky whole-
    // statement scope (which carries ALL CTEs + main-query aliases). Walkers
    // climb this parent, so filtering it here is what ends the leak — the
    // walkers stay untouched.
    const cteParent = buildCteDefParentScope(buffer, parent, visible, outerPseudo);
    const frameCtes = [...visible, ...innerCtes];
    const reachable = buildReachableTables(aliases, frameCtes, [], buffer.schemaSnapshot, /*includeSchemaTables*/ false);
    return {
      parent: cteParent,
      aliases,
      ctes: frameCtes,
      reachableTables: reachable,
      currentSchema: parent.currentSchema,
      currentDatabase: parent.currentDatabase,
      scopeKind: frame.kind,
    };
  }

  // Plain subquery frame. A LATERAL / CROSS|OUTER APPLY body may reference only
  // FROM items declared BEFORE it (decision 3) — hand it a parent filtered to
  // pre-frame aliases. A non-lateral subquery (scalar in SELECT list, `IN (…)`,
  // bare derived table) keeps the full parent so correlation never regresses.
  const frameParent = frame.lateral
    ? filterParentForLateral(parent, frame.bodyRange.startOffset)
    : parent;
  const reachable = buildReachableTables(aliases, innerCtes, [], buffer.schemaSnapshot, /*includeSchemaTables*/ false);
  return {
    parent: frameParent,
    aliases,
    ctes: innerCtes,
    reachableTables: reachable,
    currentSchema: parent.currentSchema,
    currentDatabase: parent.currentDatabase,
    scopeKind: frame.kind,
  };
}

// The filtered parent a cte-def frame climbs into: schema tables + visible
// CTEs (earlier siblings + self) + pseudo-tables, and NO main-query aliases.
// scopeKind stays 'statement' and it terminates the chain (no `.parent`), so a
// CTE body resolves as [cte-def, statement] — the outer statement's aliases
// never leak in.
function buildCteDefParentScope(
  buffer: ParsedBuffer,
  parent: Scope,
  visibleCtes: readonly CteBinding[],
  outerPseudo: readonly AliasBinding[],
): Scope {
  const reachable = buildReachableTables(
    [], visibleCtes, outerPseudo, buffer.schemaSnapshot, /*includeSchemaTables*/ true,
  );
  return {
    aliases: [...outerPseudo],
    ctes: visibleCtes,
    reachableTables: reachable,
    currentSchema: parent.currentSchema,
    currentDatabase: parent.currentDatabase,
    scopeKind: 'statement',
  };
}

// CTEs declared earlier in the same WITH clause are visible within a later
// CTE body. Returns the prefix whose body ends before (or at) `beforeOffset`
// — which also excludes SELF (self's body ends after its own start).
function mergeCtesUpTo(outerCtes: readonly CteBinding[], beforeOffset: number): CteBinding[] {
  return outerCtes.filter((c) => c.bodyRange.endOffset <= beforeOffset);
}

function buildReachableTables(
  aliases: readonly AliasBinding[],
  ctes: readonly CteBinding[],
  pseudo: readonly AliasBinding[],
  snapshot: ParsedBuffer['schemaSnapshot'],
  includeSchemaTables: boolean,
): ReachableTable[] {
  const out: ReachableTable[] = [];
  for (const a of aliases) {
    const resolvedRelation = resolveAliasRelation(a, ctes, snapshot);
    out.push({
      qualified: a.target,
      via: a.source === 'cte' ? 'cte' : 'alias',
      aliasRef: a,
      ...(resolvedRelation ? { resolvedRelation } : {}),
    });
  }
  for (const c of ctes) {
    out.push({ qualified: c.name, via: 'cte' });
  }
  for (const p of pseudo) {
    out.push({ qualified: p.target, via: 'pseudo', aliasRef: p });
  }
  if (includeSchemaTables && snapshot) {
    for (const t of snapshot.tables.values()) {
      out.push({ qualified: t.qualified, via: 'direct' });
    }
  }
  return out;
}

function buildEmptyScope(
  currentSchema: string | undefined,
  currentDatabase: string | undefined,
  snapshot: ParsedBuffer['schemaSnapshot'],
): Scope {
  const reachable = buildReachableTables([], [], [], snapshot, true);
  return {
    aliases: [],
    ctes: [],
    reachableTables: reachable,
    currentSchema: currentSchema ?? undefined,
    currentDatabase: currentDatabase ?? undefined,
    scopeKind: 'statement',
  };
}

// Procedural hand-off: `dispatchProceduralBodyClassify` (plan16 T10 — the
// per-dialect `ProceduralResolver` attachment/registry hand-off this comment
// used to describe was dead code and was deleted) only ever returns
// `'procedural-body' | null`, and BOTH map to `null` immediately below — so
// the `return { ...parent, scopeKind: 'procedural-body', parent }` branch is
// statically unreachable today (confirmed by trace, matches
// .superpowers/sdd/plan16/task-10-review.md finding M2). Left in place rather
// than deleted at plan16 T11: removing it would also require dropping the
// now-unused `parent` param and touching this seam's single call site, in a
// file Plan 19 concurrently edits nearby — recorded as a follow-up in
// .superpowers/sdd/plan16-docs-delta.md rather than risked here. v1 callers
// (suggest-*, classify) are served by the ResolverPositionKind hand-off.
function delegateProceduralScope(
  buffer: ParsedBuffer,
  cursor: SqlPosition,
  parent: Scope,
): Scope | null {
  const cls = dispatchProceduralBodyClassify(buffer, cursor);
  if (cls === null || cls === 'procedural-body') return null;
  return { ...parent, scopeKind: 'procedural-body', parent };
}
